import gift from '_assets/img/icons/svg/exploding-gift.svg';
import yarnBooks from '_assets/img/icons/svg/yarn-books.svg';
import Button from '_atoms/Button/Button';
import i18n from '_i18n';
import {
    ISubscriptionPrices,
    subcriptionPrices,
    trialDuration,
} from 'Components/Shopify/Utils';
import { useSubscriptionCurrency } from 'Hooks/useSubscriptionCurrency';
import React from 'react';
import './TrialInformationScreen.scss';
import ReferralContextService from '_services/Referral';

interface TrialInformationScreenProps {
    onAnswer: (answerIndexes: number[]) => void;
    isAbNewPriceUS?: boolean;
}

const texts: (
    localizedPrices: ISubscriptionPrices,
    isReferral: boolean,
) => Record<
    LanguageCode,
    {
        heading: string;
        subheading: string;
        subheadingPurple?: string;
        discount?: string;
    }
> = (localizedPrices, isReferral) => {
    if (isReferral) {
        return {
            no: {
                heading: `Du får 30 dager gratis prøveperiode`,
                subheading: `deretter ${localizedPrices.yearlyPricePrMonth}/mnd<br/>${localizedPrices.yearlyPrice} betales en gang i året`,
                discount:
                    'Avslutter du innen 30 dager<br/>betaler du ingenting.',
            },
            en: {
                heading: `You get a 30-day free trial`,
                subheading: `then ${localizedPrices.yearlyPricePrMonth}/month<br/>${localizedPrices.yearlyPrice} billed once per year`,
                discount:
                    'If you cancel within 30 days, you<br/>won’t be charged.',
            },
        };
    }
    return {
        no: {
            heading: `Start med ${trialDuration} dager gratis<br/>prøveperiode`,
            subheading: 'Full tilgang. Ingen betaling i dag.',
            discount: `${localizedPrices.yearlyPricePrMonth}/mnd etter prøveperioden<br/>(${localizedPrices.yearlyPrice} betales årlig)`,
        },
        en: {
            heading: `Start with a ${trialDuration}-day free trial`,
            subheading: 'Full access. No payment today.',
            discount: `${localizedPrices.yearlyPricePrMonth}/month after the trial<br/>(${localizedPrices.yearlyPrice} billed annually)`,
        },
    };
};

const TrialInformationScreen: React.FC<TrialInformationScreenProps> = ({
    onAnswer,
}) => {
    const language: LanguageCode = i18n.language === 'no' ? 'no' : 'en';
    const t = i18n.t;
    const normalizedCurrency = useSubscriptionCurrency();
    const isReferral = ReferralContextService.isReferred();

    const localizedPrices =
        subcriptionPrices[normalizedCurrency] ?? subcriptionPrices.USD;
    const { heading, subheading, subheadingPurple, discount } = texts(
        localizedPrices,
        isReferral,
    )[language];

    return (
        <div className="trial-information-screen">
            <div className="content-container">
                <div className="gift-img-wrapper">
                    <img
                        src={isReferral ? gift : yarnBooks}
                        alt={'heart'}
                        className="heart-img"
                    />
                </div>
                <div
                    className="information-heading"
                    dangerouslySetInnerHTML={{ __html: heading }}
                ></div>
                <div
                    className="information-subheading"
                    dangerouslySetInnerHTML={{ __html: subheading }}
                ></div>
                {subheadingPurple && (
                    <div
                        className="information-subheading-purple"
                        dangerouslySetInnerHTML={{ __html: subheadingPurple }}
                    ></div>
                )}
                {discount && (
                    <>
                        <div className="information-divider" />
                        <div
                            className="information-subheading"
                            dangerouslySetInnerHTML={{ __html: discount }}
                        />
                    </>
                )}
            </div>
            <div className="navigation-container">
                <Button classes="next-button" onClick={() => onAnswer([])}>
                    {t('Continue')}
                </Button>
            </div>
        </div>
    );
};

export default TrialInformationScreen;
